
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, Plus, Calendar } from "lucide-react";
import { UnavailableTimeSlot, UnavailableSlotsMap } from "@/hooks/use-calendar-settings";

interface UnavailableSlotsTabProps {
  localUnavailableSlots: UnavailableSlotsMap;
  workDays: number[];
  onSubmit: () => void;
  onSlotsChange: (slots: UnavailableSlotsMap) => void;
}

const weekDays = [
  { id: 1, name: "Lunedì" },
  { id: 2, name: "Martedì" },
  { id: 3, name: "Mercoledì" },
  { id: 4, name: "Giovedì" },
  { id: 5, name: "Venerdì" },
  { id: 6, name: "Sabato" },
  { id: 7, name: "Domenica" },
];

const availableHours = Array.from({ length: 24 }).map((_, i) => i);

const UnavailableSlotsTab: React.FC<UnavailableSlotsTabProps> = ({
  localUnavailableSlots,
  workDays,
  onSubmit,
  onSlotsChange
}) => {
  const [selectedDay, setSelectedDay] = useState<number>(1);
  const [unavailableStartHour, setUnavailableStartHour] = useState<number>(9);
  const [unavailableEndHour, setUnavailableEndHour] = useState<number>(12);
  const [applyToAllDays, setApplyToAllDays] = useState<boolean>(false);

  const addUnavailableSlot = () => {
    if (unavailableStartHour >= unavailableEndHour) {
      return; // Non aggiungere slot non validi
    }

    const newSlot: UnavailableTimeSlot = {
      start: unavailableStartHour,
      end: unavailableEndHour
    };

    if (applyToAllDays) {
      const updatedSlots = { ...localUnavailableSlots };
      
      // Applica lo slot a tutti i giorni lavorativi
      workDays.forEach(day => {
        const dayStr = day.toString();
        if (!updatedSlots[dayStr]) {
          updatedSlots[dayStr] = [];
        }
        
        // Verifica se lo slot è già presente
        const isSlotExists = updatedSlots[dayStr].some(
          existingSlot => existingSlot.start === newSlot.start && existingSlot.end === newSlot.end
        );
        
        if (!isSlotExists) {
          updatedSlots[dayStr] = [...updatedSlots[dayStr], newSlot];
        }
      });
      
      onSlotsChange(updatedSlots);
    } else {
      const dayStr = selectedDay.toString();
      const daySlots = localUnavailableSlots[dayStr] || [];
      
      // Verifica se lo slot è già presente
      const isSlotExists = daySlots.some(
        existingSlot => existingSlot.start === newSlot.start && existingSlot.end === newSlot.end
      );
      
      if (isSlotExists) {
        return; // Lo slot esiste già, non fare nulla
      }
      
      const updatedSlots = {
        ...localUnavailableSlots,
        [dayStr]: [...daySlots, newSlot]
      };
      
      onSlotsChange(updatedSlots);
    }
  };

  const removeUnavailableSlot = (day: string, index: number) => {
    const updatedSlots = { ...localUnavailableSlots };
    
    if (updatedSlots[day] && updatedSlots[day].length > index) {
      updatedSlots[day] = updatedSlots[day].filter((_, i) => i !== index);
      
      // Se l'array è vuoto, rimuovi la chiave
      if (updatedSlots[day].length === 0) {
        delete updatedSlots[day];
      }
    }
    
    onSlotsChange(updatedSlots);
  };

  const clearUnavailableSlots = (day?: string) => {
    if (!day) {
      onSlotsChange({});
    } else {
      const updatedSlots = { ...localUnavailableSlots };
      delete updatedSlots[day];
      onSlotsChange(updatedSlots);
    }
  };

  const getDayUnavailableSlots = (day: number): UnavailableTimeSlot[] => {
    const dayStr = day.toString();
    return localUnavailableSlots[dayStr] || [];
  };

  const formatTimeSlot = (slot: UnavailableTimeSlot): string => {
    return `${slot.start}:00 - ${slot.end}:00`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Giorno
            </label>
            <Select
              value={selectedDay.toString()}
              onValueChange={value => setSelectedDay(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleziona giorno" />
              </SelectTrigger>
              <SelectContent>
                {weekDays.map(day => (
                  <SelectItem key={day.id} value={day.id.toString()}>
                    {day.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Ora inizio
              </label>
              <Select
                value={unavailableStartHour.toString()}
                onValueChange={value => setUnavailableStartHour(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Ora inizio" />
                </SelectTrigger>
                <SelectContent>
                  {availableHours.map(hour => (
                    <SelectItem 
                      key={`start-${hour}`} 
                      value={hour.toString()}
                      disabled={hour >= unavailableEndHour}
                    >
                      {hour}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Ora fine
              </label>
              <Select
                value={unavailableEndHour.toString()}
                onValueChange={value => setUnavailableEndHour(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Ora fine" />
                </SelectTrigger>
                <SelectContent>
                  {availableHours.map(hour => (
                    <SelectItem 
                      key={`end-${hour}`} 
                      value={hour.toString()}
                      disabled={hour <= unavailableStartHour}
                    >
                      {hour}:00
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox 
              id="apply-all" 
              checked={applyToAllDays} 
              onCheckedChange={checked => setApplyToAllDays(checked === true)}
            />
            <label 
              htmlFor="apply-all" 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Applica a tutti i giorni lavorativi
            </label>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button 
              type="button" 
              onClick={addUnavailableSlot}
              disabled={unavailableStartHour >= unavailableEndHour}
              className="mt-2"
            >
              <Plus className="h-4 w-4 mr-1" /> Aggiungi fascia
            </Button>
            
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => clearUnavailableSlots(selectedDay.toString())}
              className="mt-2"
            >
              Rimuovi tutte le fasce per {weekDays.find(d => d.id === selectedDay)?.name}
            </Button>
          </div>
        </div>
        
        <div className="border rounded-md p-4 bg-muted/20 space-y-4">
          <h3 className="font-medium flex items-center">
            <Calendar className="h-4 w-4 mr-2" /> 
            Fasce non disponibili
          </h3>
          
          {Object.keys(localUnavailableSlots).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna fascia non disponibile configurata</p>
          ) : (
            <div className="space-y-4">
              {weekDays.map(day => {
                const slots = getDayUnavailableSlots(day.id);
                if (slots.length === 0) return null;
                
                return (
                  <div key={day.id} className="space-y-2">
                    <h4 className="text-sm font-medium">{day.name}</h4>
                    <div className="flex flex-wrap gap-2">
                      {slots.map((slot, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary"
                          className="flex items-center gap-1 px-2 py-1"
                        >
                          {formatTimeSlot(slot)}
                          <button
                            onClick={() => removeUnavailableSlot(day.id.toString(), index)}
                            className="h-4 w-4 rounded-full hover:bg-muted/50 inline-flex items-center justify-center"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              <div className="pt-2">
                <Button 
                  type="button" 
                  variant="destructive" 
                  onClick={() => clearUnavailableSlots()}
                  size="sm"
                  className="mt-2"
                >
                  Rimuovi tutte le fasce
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <DialogFooter>
        <Button onClick={onSubmit}>Salva impostazioni</Button>
      </DialogFooter>
    </div>
  );
};

export default UnavailableSlotsTab;
